import React, { useEffect, useState } from "react";
import { Button, Empty, Grid, Input, Table, Tooltip } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import {
  CloseCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { ExcelComponent, PdfElement } from "../../../../../libs/shared";
import "../../css/dash-table.css";

export interface DashboardSortingI {
  id: string;
  desc: boolean;
}

export interface DashboardPaginationI {
  pageIndex: number;
  pageSize: number;
}

interface DashboardTablePropsI<T> {
  columns: ColumnsType<T>;
  data: T[];
  total: number;
  loading: boolean;
  pagination: DashboardPaginationI;
  setPagination: (value: DashboardPaginationI) => void;
  sorting: DashboardSortingI[];
  setSorting: (value: DashboardSortingI[]) => void;
  setGlobalFilter: (value: string) => void;
  onRefresh: () => void;
  renderActions: (row: T) => React.ReactNode;
  actionsWidth?: number;
  exportTitle: string;
  toolbarExtra?: React.ReactNode;
}

const DEFAULT_SORTING: DashboardSortingI[] = [{ id: "id", desc: true }];

export const DashboardTable = <T extends { id?: number | string | null }>({
  columns,
  data,
  total,
  loading,
  pagination,
  setPagination,
  sorting,
  setSorting,
  setGlobalFilter,
  onRefresh,
  renderActions,
  actionsWidth = 130,
  exportTitle,
  toolbarExtra,
}: DashboardTablePropsI<T>) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const [search, setSearch] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  const [isReadyExport, setReadyExport] = useState(false);

  useEffect(() => {
    setSelectedKeys([]);
    setSelectedRows([]);
    setReadyExport(false);
  }, [data]);

  const exportHeaders = columns.map((col: any) => ({
    label: typeof col.title === "string" ? col.title : String(col.key ?? ""),
    id: col.dataIndex ?? col.key,
    width: typeof col.width === "number" ? col.width : 150,
    textAlign: "left",
    show: true,
    fontSize: "10px",
  }));

  const sortedColumns: ColumnsType<T> = columns.map((col: any) => {
    const field = col.dataIndex ?? col.key;
    const sortable = col.sorter !== false;
    return {
      ...col,
      key: col.key ?? field,
      sorter: sortable,
      sortOrder:
        sortable && sorting[0]?.id === field
          ? sorting[0].desc
            ? "descend"
            : "ascend"
          : null,
    };
  });

  const actionsColumn = {
    title: "Acciones",
    key: "actions",
    align: "center" as const,
    fixed: "right" as const,
    width: actionsWidth,
    render: (_: unknown, row: T) => renderActions(row),
  };

  const handleTableChange = (
    pag: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: { action: string }
  ) => {
    if (extra.action === "sort") {
      const current = Array.isArray(sorter) ? sorter[0] : sorter;
      setSorting(
        current?.order
          ? [
              {
                id: String(current.columnKey ?? current.field),
                desc: current.order === "descend",
              },
            ]
          : DEFAULT_SORTING
      );
      return;
    }
    setPagination({
      pageIndex: (pag.current ?? 1) - 1,
      pageSize: pag.pageSize ?? pagination.pageSize,
    });
  };

  const handleSearch = (value: string) => {
    setGlobalFilter(value.trim());
    setPagination({ ...pagination, pageIndex: 0 });
  };

  return (
    <div className="dash-table">
      <div className="dash-table-toolbar">
        <Input.Search
          className="dash-table-search"
          allowClear
          placeholder="Buscar..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value === "") handleSearch("");
          }}
          onSearch={handleSearch}
        />
        {toolbarExtra}
        <span className="dash-table-spacer" />
        <Tooltip title="Refrescar">
          <Button icon={<ReloadOutlined />} onClick={onRefresh} />
        </Tooltip>
        {!isMobile ? (
          <div className="dash-table-export">
            <Tooltip title="Exportar seleccionados">
              <Button
                icon={<DownloadOutlined />}
                disabled={selectedRows.length === 0 || isReadyExport}
                onClick={() => setReadyExport(true)}
              >
                Exportar
              </Button>
            </Tooltip>
            {isReadyExport ? (
              <>
                <PdfElement
                  data={selectedRows}
                  headersColumn={exportHeaders}
                  HandleClickPdf={() =>
                    setTimeout(() => setReadyExport(false), 1500)
                  }
                  isSmall={true}
                />
                <ExcelComponent
                  data={selectedRows}
                  titulo={exportTitle}
                  headersColumn={exportHeaders}
                  isEcuafact={false}
                />
                <Tooltip title="Cancelar exportación">
                  <Button
                    danger
                    type="text"
                    icon={<CloseCircleOutlined />}
                    onClick={() => setReadyExport(false)}
                  />
                </Tooltip>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <Table<T>
        size="small"
        rowKey={(row) => String(row.id)}
        columns={[...sortedColumns, actionsColumn]}
        dataSource={data}
        loading={loading}
        rowSelection={
          isMobile
            ? undefined
            : {
                selectedRowKeys: selectedKeys,
                onChange: (keys, rows) => {
                  setSelectedKeys(keys);
                  setSelectedRows(rows);
                },
              }
        }
        scroll={{ x: "max-content", y: "max(280px, calc(100vh - 640px))" }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sin registros"
            />
          ),
        }}
        pagination={{
          current: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          total,
          size: "small",
          simple: isMobile,
          showSizeChanger: !isMobile,
          pageSizeOptions: [10, 15, 25, 50],
          showTotal: isMobile
            ? undefined
            : (count, range) => `${range[0]}-${range[1]} de ${count}`,
        }}
        onChange={handleTableChange as any}
      />
    </div>
  );
};
